import { biToast } from '@pulsepointinc/bi-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import { queryClient } from '../../lib';
import { type ApiError, type ApiErrorV7, CustomApiError } from '../../lib/api';
import { workspaceTypeChecks } from '../../lib/api/helpers';
import {
  type DownloadReachAnalysisRequest,
  downloadReachAnalysis,
  type GetAvailableAttributesResponse,
  type GetColumnSuggestionsRequest,
  type GetColumnSuggestionsResponse,
  type GetColumnsRequest,
  type GetColumnsResponse,
  type GetPreviewRequest,
  type GetPreviewResponse,
  type GetReservedColumnsResponse,
  getAvailableAttributes,
  getColumnSuggestions,
  getColumns,
  getPreview,
  getReservedColumns,
  type MapDataRequest,
  type MapDataResponse,
  mapData,
  type PublishRequest,
  type PublishResponse,
  publish,
  type SegmentationRequest,
  type SegmentationResponse,
  updateSegmentation,
} from '../../lib/api/listMatching';
import {
  downloadReachAnalysis as downloadReachAnalysisConsent,
  getAvailableAttributes as getAvailableAttributesConsent,
  getColumnSuggestions as getColumnSuggestionsConsent,
  getColumns as getColumnsConsent,
  getPreview as getPreviewConsent,
  getReservedColumns as getReservedColumnsConsent,
  mapData as mapDataConsent,
  publish as publishConsent,
  SOFT_MATCH_ATTRIBUTES_SOURCE,
  type SoftMatchProcessRequest,
  type SoftMatchProcessResponse,
  softMatchProcess,
  updateSegmentation as updateSegmentationConsent,
} from '../../lib/api/listMatchingConsent';
import {
  type GetDeliveryIdDetailsRequest,
  type GetDeliveryIdDetailsResponse,
  type GetDictionariesRequest,
  type GetDictionariesResponse,
  type GetMedscapeConsentDictionariesRequest,
  getDeliveryIdDetails,
  getDictionaries,
  getMedscapeConsentDictionaries,
} from '../../lib/api/medscape';
import { type IMedscapeMetadata, WorkspaceTypeID } from '../../lib/api/workspaces';
import { API_TAGS } from '../../lib/constants';
import { useCurrentWorkspace } from '../useCurrentWorkspace';

export enum LM_API_TAGS {
  ListMatchingColumns = 'listMatchingColumns',
  ListMatchingAvailableAttributes = 'listMatchingAvailableAttributes',
  ListMatchingColumnSuggestions = 'listMatchingColumnSuggestions',
  ListMatchingReservedColumns = 'getListMatchingReservedColumns',
  ListMatchingMapData = 'ListMatchingMapData',
  ListMatchingSoftMatchProcess = 'ListMatchingSoftMatchProcess',
  ListMatchingSegmentation = 'ListMatchingSegmentation',
  ListMatchingPreview = 'ListMatchingPreview',
  ListMatchingPublish = 'publishListMatching',
  ListMatchingPublishDryRun = 'publishDryRunValidation',
  ListMatchingDownload = 'downloadReachAnalysis',
  Dictionaries = 'medscapeDictionaries',
  DeliveryIdDetails = 'medscapeDeliveryIdDetails',
}

export function useIsConsentWorkspace() {
  const { currentWorkspace } = useCurrentWorkspace();
  return workspaceTypeChecks.isConsentLikeWorkspace(currentWorkspace?.workspace_type_id);
}

export function useIsSoftMatchWorkspace() {
  const { currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.workspace_type_id === WorkspaceTypeID.MedscapeSoftMatch;
}

export function useGetListMatchingColumnsQuery(
  params: GetColumnsRequest,
  options: { enabled: boolean; staleTime?: number } = { enabled: true },
) {
  const isConsent = useIsConsentWorkspace();
  return useQuery<GetColumnsResponse>({
    queryKey: [LM_API_TAGS.ListMatchingColumns, params, isConsent],
    queryFn: () => (isConsent ? getColumnsConsent(params) : getColumns(params)),
    enabled: options.enabled,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
    refetchOnWindowFocus: false,
  });
}

export function useGetListMatchingAvailableAttributesQuery(
  options: { enabled: boolean; staleTime?: number } = { enabled: true },
) {
  const { currentWorkspace } = useCurrentWorkspace();
  const isConsent = useIsConsentWorkspace();
  const isSoftMatch = useIsSoftMatchWorkspace();
  const params = isSoftMatch
    ? { source: SOFT_MATCH_ATTRIBUTES_SOURCE, workspaceID: currentWorkspace?.workspace_id }
    : undefined;

  return useQuery<GetAvailableAttributesResponse>({
    queryKey: [LM_API_TAGS.ListMatchingAvailableAttributes, isConsent, params],
    queryFn: () => (isConsent ? getAvailableAttributesConsent(params) : getAvailableAttributes()),
    enabled: options.enabled,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
    refetchOnWindowFocus: false,
  });
}

export function useMapListMatchingDataMutation(medscapeData?: IMedscapeMetadata) {
  const isConsent = useIsConsentWorkspace();
  const mutation = useMutation<MapDataResponse, unknown, MapDataRequest>({
    mutationFn: (body) => (isConsent ? mapDataConsent(body, medscapeData) : mapData(body, medscapeData)),
    mutationKey: [LM_API_TAGS.ListMatchingMapData],
    onError: (error) => {
      // TODO: to remove type castings after full v7 migration
      if (error instanceof CustomApiError) {
        const message =
          (error as unknown as ApiErrorV7)?.data?.errorDesc ?? error?.message ?? 'Unable to map list matching data.';
        biToast.error(message);
      } else {
        biToast.error('Unable to map list matching data.');
      }
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export interface SoftMatchProcessVariables {
  body: SoftMatchProcessRequest;
  medscapeData?: IMedscapeMetadata;
}

export function useSoftMatchProcessMutation() {
  const mutation = useMutation<SoftMatchProcessResponse, unknown, SoftMatchProcessVariables>({
    mutationFn: ({ body, medscapeData }) => softMatchProcess(body, medscapeData),
    mutationKey: [LM_API_TAGS.ListMatchingSoftMatchProcess],
    onError: (error) => {
      const message =
        error instanceof CustomApiError
          ? ((error as unknown as ApiErrorV7)?.data?.errorDesc ?? error?.message)
          : undefined;
      biToast.error(message ?? 'Unable to process soft match.');
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
  };
}

export function useGetListMatchingColumnSuggestionsQuery(
  params: GetColumnSuggestionsRequest,
  options: { enabled: boolean; staleTime?: number } = { enabled: true },
) {
  const isConsent = useIsConsentWorkspace();
  const isSoftMatch = useIsSoftMatchWorkspace();
  const consentParams = isSoftMatch ? { ...params, source: SOFT_MATCH_ATTRIBUTES_SOURCE } : params;

  return useQuery<GetColumnSuggestionsResponse>({
    queryKey: [LM_API_TAGS.ListMatchingColumnSuggestions, consentParams, isConsent],
    queryFn: () => (isConsent ? getColumnSuggestionsConsent(consentParams) : getColumnSuggestions(params)),
    enabled: options.enabled,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
    refetchOnWindowFocus: false,
  });
}

export function useUpdateListMatchingSegmentsMutation(medscapeData?: IMedscapeMetadata) {
  const isConsent = useIsConsentWorkspace();
  const mutation = useMutation<SegmentationResponse, unknown, SegmentationRequest>({
    mutationFn: (body) =>
      isConsent ? updateSegmentationConsent(body, medscapeData) : updateSegmentation(body, medscapeData),
    mutationKey: [LM_API_TAGS.ListMatchingSegmentation],
    onError: () => {
      biToast.error('Unable to update segments. Please try again.');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [LM_API_TAGS.ListMatchingPreview],
      });
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useListMatchingPreviewQuery(params: GetPreviewRequest) {
  const isConsent = useIsConsentWorkspace();
  return useQuery<GetPreviewResponse>({
    queryKey: [API_TAGS.SingleWorkspace, LM_API_TAGS.ListMatchingPreview, params, isConsent],
    queryFn: () => (isConsent ? getPreviewConsent(params) : getPreview(params)),
    enabled: Object.values(params).every(Boolean),
  });
}

export function useListMatchingReservedColumnsQuery() {
  const isConsent = useIsConsentWorkspace();
  return useQuery<GetReservedColumnsResponse>({
    queryKey: [LM_API_TAGS.ListMatchingReservedColumns, isConsent],
    queryFn: () => (isConsent ? getReservedColumnsConsent() : getReservedColumns()),
  });
}

export function usePublishDryRunValidation(requestDetails: PublishRequest, options: { enabled?: boolean }) {
  const isConsent = useIsConsentWorkspace();
  return useQuery<PublishResponse, ApiError>({
    queryKey: [LM_API_TAGS.ListMatchingPublishDryRun, requestDetails, isConsent],
    queryFn: () =>
      isConsent
        ? publishConsent({ ...requestDetails, pillowHumping: true })
        : publish({ ...requestDetails, pillowHumping: true }),
    enabled: options.enabled ?? false,
    retry: 0,
  });
}

export function usePublishListMatchingMutation() {
  const isConsent = useIsConsentWorkspace();
  return useMutation<PublishResponse, unknown, PublishRequest>({
    mutationKey: [LM_API_TAGS.ListMatchingPublish],
    mutationFn: (params) => (isConsent ? publishConsent(params) : publish(params)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [API_TAGS.SingleWorkspace],
      });
    },
  });
}

export function useDownloadReachAnalysisMutation() {
  const isConsent = useIsConsentWorkspace();
  return useMutation<string, unknown, DownloadReachAnalysisRequest>({
    mutationKey: [LM_API_TAGS.ListMatchingDownload],
    mutationFn: (params) => (isConsent ? downloadReachAnalysisConsent(params) : downloadReachAnalysis(params)),
    onError: () => {
      biToast.error('Unable to download reach analysis. Please try again.');
    },
  });
}

// TODO: to remove isLegacy after full v7 migration (for LM)
export function useMedscapeDictionariesQuery(
  params: GetDictionariesRequest | GetMedscapeConsentDictionariesRequest,
  options?: { enabled?: boolean; staleTime?: number; isLegacy?: boolean },
) {
  const isConsent = useIsConsentWorkspace();

  return useQuery<GetDictionariesResponse>({
    queryKey: [LM_API_TAGS.Dictionaries, params, isConsent],
    queryFn: () => {
      if (isConsent && !options?.isLegacy) {
        return getMedscapeConsentDictionaries(params as GetMedscapeConsentDictionariesRequest);
      }
      return getDictionaries(params as GetDictionariesRequest);
    },
    enabled: options?.enabled,
    ...(!isNil(options?.staleTime) ? { staleTime: options.staleTime } : {}),
    refetchOnWindowFocus: false,
  });
}

export function useDeliveryIdDetailsMutation() {
  return useMutation<GetDeliveryIdDetailsResponse, Error, GetDeliveryIdDetailsRequest>({
    mutationKey: [LM_API_TAGS.DeliveryIdDetails],
    mutationFn: getDeliveryIdDetails,
  });
}
